import React, { useEffect, useMemo, useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import BootstrapTable, {
    TableChangeState,
    TableColumnFilterProps,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory, {
    reactMultiselectFilter,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import createNotification from '../../../../utils/createNotification';
import './MarketplaceOpportunities.scss';
import { Button, Col } from 'reactstrap';
import ConfirmationDialogWithReason from '../../../../components/ConfirmationDialog/confirmation-dialog-with-reason';
import { MarketplaceOpportunity } from '../../../../slices/marketplace/opportunity/models/opportunity';
import {
    MarketplaceOpportunityStatus,
    MarketplaceOpportunityStatusList,
    MarketplaceOpportunityStatusOptions,
} from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import { GetOpportunities } from '../../../../slices/marketplace/opportunity/thunks/get-opportunities.thunk';
import { getFilteredAndSortedOpportunities } from '../../../../slices/marketplace/opportunity/helpers/opportunities-table-helpers';
import { useDispatch } from 'react-redux';
import {
    setSelectedOpportunity,
    setPagination,
    setSortOrder,
    setFilters,
    navigateTableToOpportunity,
} from '../../../../slices/marketplace/opportunity/opportunity.slice';
import { GetStatesByRegion } from '../../../../slices/locations-slice/thunks/get-states-by-region.thunk';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { GetBuyer } from '../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import LandscapeMapContainer, {
    Region,
    regionConfigs,
} from '../../../LandscapeMapContainer/LandscapeMapContainer';
import { MarkerData } from '../../../../components/LandscapeMap/LandscapeMapTypes';
import { ShapeType } from '../../../../components/LandscapeMap/Markers/Shapes/MarkersWrapper';
import { zipCodeConvert } from '../../../../utils/zipcodeConvertor/zipcodeConvertor';
import { OpportunityLocation } from '../../../../slices/marketplace/opportunity/models/opportunity-location';
import { useSearchParams } from 'react-router-dom';
import {
    getAllStatesIndexed,
    stateToCodeMapper,
} from '../../../../utils/locationsHelper';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { LocationTypes } from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import ReactDOMServer from 'react-dom/server';
import { SaveSelectedOpportunity } from '../../../../slices/marketplace/buyer/thunks/save-selected-opportunity.thunk';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import {
    TeamMemberPermissions,
    TeamMemberPermissionsHelper,
} from '../MyTeamMembers/TeamMemberPermissionsHelper';
import {
    ExternalTeamMemberRole,
    InternalPermissions,
    TeamMemberType,
} from '../MyTeamMembers/Models/Enums';
import SortingSymbol from '../../../../components/SortingSymbol/sorting-symbol';
import moment from 'moment';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import {
    getOtherFocus,
    getServiceFocusesForMapLegend,
    isOtherFocus,
    mapShapeToSymbol,
} from '../../../UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { MapShape } from '../../../../slices/service-focuses/models/enums/map-shape.enum';

interface MarketplaceOpportunitiesProps {
    refreshOpportunitiesToggle: boolean;
}

const MarketplaceOpportunities = (props: MarketplaceOpportunitiesProps) => {
    const summary = useStateSelector((state) => state.userSummary.summary);
    const serviceFocuses = useServiceFocuses();
    const [searchParams] = useSearchParams({});
    const opportunityId = searchParams.get('opportunityId');
    const [opportunities, setOpportunities] = useState<
        MarketplaceOpportunity[]
    >([]);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [selectedMarkers, setSelectedMarkers] = useState<MarkerData[]>([]);
    const [opportunitiesLocations, setOpportunitiesLocations] = useState<
        OpportunityLocation[]
    >([]);
    const axios = useStateSelector((state) => state.core.axios);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [reasonForRejection, setReasonForRejection] = useState('');
    const [selectedOpportunityIdToReject, setSelectedOpportunityIdToReject] =
        useState(0);
    const [selectedOpportunityIdFromMap, setSelectedOpportunityIdFromMap] =
        useState(0);
    const [rejectedPracticeName, setRejectedPracticeName] = useState('');
    const [allStates, setAllStates] = useState([]);
    const [hasNavigatedToInitial, setHasNavigatedToInitial] = useState(false);

    const engagingAvailableStatuses = [
        MarketplaceOpportunityStatus.RejectedByBuyer,
        MarketplaceOpportunityStatus.NewOpportunity,
        MarketplaceOpportunityStatus.Tagged,
    ];

    const taggingAvailableStatuses = [
        MarketplaceOpportunityStatus.RejectedByBuyer,
        MarketplaceOpportunityStatus.NewOpportunity,
    ];

    const rejectingAvailableStatuses = [
        MarketplaceOpportunityStatus.Tagged,
        MarketplaceOpportunityStatus.NewOpportunity,
        MarketplaceOpportunityStatus.Engaged,
        MarketplaceOpportunityStatus.BuyerMissedDeadline,
        MarketplaceOpportunityStatus.AwaitingBuyerResponse,
        MarketplaceOpportunityStatus.AwaitingSellerResponse,
    ];

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'listingDate',
            order: 'asc',
        },
    ];

    const [currentZoom, setCurrentZoom] = useState(
        regionConfigs[Region.Mainland].defaultZoom
    );
    const dispatch = useDispatch() as any;

    const opportunityState = useStateSelector((state) => state.opportunities);
    const statesByRegion = useStateSelector(
        (state) => state.locationsState.statesByRegion
    );
    const [isMPExternalMember, setIsMPExternalMember] = useState(false);

    useEffect(() => {
        setIsMPExternalMember(getIsMPExternalMember());
    }, [summary]);

    const getIsMPExternalMember = () => {
        return (
            summary?.associateMember?.some(
                (member: {
                    associateMemberMarketplaceMappings?: {
                        externalRole: ExternalTeamMemberRole | null;
                    }[];
                }) =>
                    member.associateMemberMarketplaceMappings?.some(
                        (mapping: {
                            externalRole: ExternalTeamMemberRole | null;
                        }) => mapping.externalRole !== null
                    )
            ) || false
        );
    };

    const fetchData = async () => {
        GetOpportunitiesLocations();
        return await dispatch(GetOpportunities());
    };

    const navigateToOpportunityPage = (opportunityId: number) => {
        dispatch(navigateTableToOpportunity(opportunityId));
        setHasNavigatedToInitial(true);
    };

    const handleTableChange = (
        type: string,
        state: TableChangeState<MarketplaceOpportunity>
    ) => {
        if (type === 'filter') {
            const newFilters: { [key: string]: any } = {};
            Object.keys(state.filters).forEach((key) => {
                const filterValue = state.filters[key].filterVal;
                if (
                    filterValue &&
                    Array.isArray(filterValue) &&
                    filterValue.length > 0
                ) {
                    newFilters[key] = filterValue;
                }
            });
            dispatch(setFilters({ filters: newFilters, serviceFocuses }));
        }

        if (type === 'pagination') {
            dispatch(
                setPagination({
                    pagination: {
                        page: state.page,
                        sizePerPage: state.sizePerPage,
                    },
                    serviceFocuses,
                })
            );
        }

        if (type === 'sort') {
            dispatch(
                setSortOrder({
                    sortOrder: {
                        dataField: state.sortField,
                        order: state.sortOrder,
                    },
                    serviceFocuses,
                })
            );
        }
    };

    const highlightOpportunityMarkers = (
        opportunityId: number,
        markersData: MarkerData[]
    ) => {
        const opportunityLocation = opportunitiesLocations.find(
            (x) => x.opportunityId == opportunityId
        );

        const markerIds: number[] = [];

        opportunityLocation?.practices.forEach((practice) => {
            if (
                practice.practiceLocationData &&
                practice.practiceLocationData.length > 0
            ) {
                practice.practiceLocationData.forEach((location) => {
                    markerIds.push(location.id);
                });
            } else {
                markerIds.push(practice.practiceId);
            }
        });

        const newSelectedMarkers = markersData.filter((x) =>
            markerIds.includes(x.id)
        );
        setSelectedMarkers(newSelectedMarkers);
    };

    const handleTableRowSelect = (
        opportunityId: number,
        markersData: MarkerData[]
    ) => {
        setSelectedOpportunityIdFromMap(opportunityId);
        const opportunity = opportunityState.opportunities.opportunities.find(
            (x) => x.opportunityId == opportunityId
        );

        dispatch(SaveSelectedOpportunity(opportunityId));

        if (opportunity == null) {
            fetchData();
        } else {
            dispatch(setSelectedOpportunity(opportunityId));
            dispatch(navigateTableToOpportunity(opportunityId));
        }

        highlightOpportunityMarkers(opportunityId, markersData);
    };

    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );

    useEffect(() => {
        if (statesByRegion && statesByRegion.length > 0) {
            const allStatesIndexed = getAllStatesIndexed(statesByRegion);
            setAllStates(allStatesIndexed);
        }
    }, [statesByRegion]);

    useEffect(() => {
        setOpportunities(opportunityState.opportunities.opportunities);

        if (selectedOpportunityIdFromMap !== 0) {
            dispatch(setSelectedOpportunity(selectedOpportunityIdFromMap));
        }
    }, [opportunityState.opportunities, allStates]);

    useEffect(() => {
        if (
            !hasNavigatedToInitial &&
            selectedOpportunityIdFromMap &&
            opportunities.length > 0
        ) {
            navigateToOpportunityPage(selectedOpportunityIdFromMap);
        }
    }, [opportunities, hasNavigatedToInitial]);

    useEffect(() => {
        mapMarkers();
    }, [opportunityState.opportunities, allStates, opportunitiesLocations]);

    useEffect(() => {
        dispatch(GetStatesByRegion());
        fetchData();

        fetchProfile().then((buyer) => {
            const initialOpportunityId =
                opportunityId ??
                buyer.selectedOpportunityId?.toString() ??
                null;

            if (initialOpportunityId) {
                const oppId = Number(initialOpportunityId);
                setSelectedOpportunityIdFromMap(oppId);
                dispatch(setSelectedOpportunity(oppId));
            }
        });
    }, []);

    useEffect(() => {
        fetchData();
    }, [props.refreshOpportunitiesToggle]);

    useEffect(() => {
        if (
            opportunityId &&
            selectedOpportunity &&
            markers?.length > 0 &&
            opportunitiesLocations?.length > 0 &&
            opportunityState.opportunities?.opportunities?.length > 0
        ) {
            highlightOpportunityMarkers(
                selectedOpportunity.opportunityId,
                markers
            );
        }
    }, [selectedOpportunity, markers]);

    const mapMarkers = () => {
        // Get filtered and sorted opportunities (before pagination) using the helper
        const filteredOpportunities = getFilteredAndSortedOpportunities(
            opportunityState,
            serviceFocuses
        );

        // Filter locations to only include opportunities that match current table filters
        const filteredOpportunityIds = new Set(
            filteredOpportunities.map((opp) => opp.opportunityId)
        );
        const filteredLocations = opportunitiesLocations.filter((loc) =>
            filteredOpportunityIds.has(loc.opportunityId)
        );

        const practices = filteredLocations.flatMap((x) => x.practices);
        const newMarkers: MarkerData[] = [];

        practices.forEach((practice) => {
            const opportunity = filteredLocations.find((x) =>
                x.practices.some((x) => x.practiceId == practice.practiceId)
            );

            if (
                practice.practiceLocationData &&
                practice.practiceLocationData.length > 0
            ) {
                practice.practiceLocationData.forEach((location) => {
                    const position = getMarkerPosition(location.zipCode);
                    newMarkers.push({
                        id: location.id,
                        name: location.practiceLocationName,
                        position: position,
                        color: getMarkerColor(opportunity.status),
                        shape: getShapeByPracticeType(practice.practiceType),
                        fteDVMs: location.fteDvm,
                        isIdNdaSigned: practice.isIdNdaSigned,
                        showTooltip: false,
                        onClick: (_: any) => {
                            handleTableRowSelect(
                                opportunity.opportunityId,
                                newMarkers
                            );
                        },
                    });
                });
            } else {
                const position = getMarkerPosition(practice.zipCode);
                newMarkers.push({
                    id: practice.practiceId,
                    name: practice.practiceName,
                    position: position,
                    color: getMarkerColor(opportunity.status),
                    shape: getShapeByPracticeType(practice.practiceType),
                    fteDVMs: practice.fteDvms,
                    isIdNdaSigned: practice.isIdNdaSigned,
                    showTooltip: false,
                    onClick: (_: any) => {
                        handleTableRowSelect(
                            opportunity.opportunityId,
                            newMarkers
                        );
                    },
                });
            }
        });

        setMarkers(() => newMarkers);
    };

    const getMarkerColor = (status: MarketplaceOpportunityStatus) => {
        const config = MarketplaceOpportunityStatusList[status];
        return config.color;
    };

    const getMarkerPosition = (zipCode: string): [number, number] | null => {
        try {
            return zipCodeConvert(zipCode);
        } catch {
            return [0, 0];
        }
    };

    const getShapeByPracticeType = (type: number): ShapeType => {
        const focusMapType = serviceFocuses.find((f) => f.id === type)?.mapType;
        switch (focusMapType) {
            case MapShape.RoundDot:
                return ShapeType.Circle;
            case MapShape.Pentagram:
                return ShapeType.Pentagon;
            case MapShape.Octagon:
                return ShapeType.Octagon;
            case MapShape.Diamond:
                return ShapeType.Diamond;
            case MapShape.SixPointStar:
                return ShapeType.Star6;
            case MapShape.Triangle:
                return ShapeType.Triangle;
            case MapShape.FivePointStar:
                return ShapeType.Star5;
            case MapShape.Square:
                return ShapeType.Rectangle;
            case MapShape.FourPointStar:
                return ShapeType.Star4;
            default:
                return ShapeType.Star4;
        }
    };

    const fetchProfile = async (): Promise<MarketplaceBuyer> => {
        const result = await dispatch(GetBuyer(null));
        const buyer = result.payload.buyer as MarketplaceBuyer;
        return buyer;
    };

    const renderColorsDescription = () => {
        const colorGroups: { [key: string]: string[] } = {};
        MarketplaceOpportunityStatusList.forEach((config) => {
            if (!colorGroups[config.color]) {
                colorGroups[config.color] = [];
            }
            colorGroups[config.color].push(config.name);
        });

        return Object.entries(colorGroups).map(([color, names]) => {
            return (
                <div className="legend-item">
                    <div
                        className="item-bullet"
                        style={{
                            background: color,
                        }}></div>
                    <div className="item-name">{names.join(', ')}</div>
                </div>
            );
        });
    };

    const renderShapesDescription = () => {
        return getServiceFocusesForMapLegend(serviceFocuses).map(
            (practiceFocus) => {
                const name = practiceFocus.name;
                const icon = mapShapeToSymbol(practiceFocus.mapType);
                if(practiceFocus.mapType === MapShape.RoundDot){
                    return (
                    <div className="legend-item">
                        <div
                            className="item-bullet"
                            style={{
                                background: '#7d7d7d',
                            }}
                        />
                        <div className="item-name">{name}</div>
                    </div>
                );
                }
                return (
                    <div className="legend-item">
                        <div className="item-bullet">{icon}</div>
                        <div className="item-name">{name}</div>
                    </div>
                );
            }
        );
    };

    const GetOpportunitiesLocations = () => {
        axios
            .get(
                `/api/Marketplace/MarketplaceOpportunities/GetOpportunitiesLocations`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during fetching locations.',
                        'error'
                    );
                } else {
                    setOpportunitiesLocations(response.data);
                }
            });
    };

    const engage = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/Engage`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during engaging.',
                        'error'
                    );
                } else {
                    fetchData().then(() => {
                        dispatch(SaveSelectedOpportunity(opportunityId));
                        dispatch(setSelectedOpportunity(opportunityId));
                    });
                }
            });
    };

    const reject = () => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${selectedOpportunityIdToReject}/Reject`,
                {
                    leaveComment: reasonForRejection,
                }
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during rejection.',
                        'error'
                    );
                } else {
                    fetchData();
                }
            })
            .finally(() => {
                setReasonForRejection('');
            });
    };

    const tag = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/tag`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during tagging opportuinity.',
                        'error'
                    );
                } else {
                    fetchData();
                }
            });
    };

    const untag = (opportunityId: number) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${opportunityId}/untag`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during untagging opportuinity.',
                        'error'
                    );
                } else {
                    fetchData();
                }
            });
    };

    const locationFilterProps = reactMultiselectFilter({
        placeholder: '',
        options: allStates.reduce(
            (acc, state) => ({ ...acc, [state.code]: state.code }),
            {}
        ),
    }) as TableColumnFilterProps;

    const opportunityStatusFilterProps = reactMultiselectFilter({
        placeholder: '',
        options: MarketplaceOpportunityStatusOptions,
    }) as TableColumnFilterProps;

    const fteDvmOptions = useMemo(() => {
        if (!opportunities || opportunities.length === 0) {
            return { NA: 'NA' };
        }

        const uniqueFteDvms = new Set<string>();
        opportunities.forEach((opportunity) => {
            const fteDvm = opportunity.engagementFteDvms;
            if (fteDvm !== null && fteDvm !== undefined) {
                uniqueFteDvms.add(fteDvm.toString());
            } else {
                uniqueFteDvms.add('NA');
            }
        });

        const sortedValues = Array.from(uniqueFteDvms).sort((a, b) => {
            if (a === 'NA') return 1;
            if (b === 'NA') return -1;
            return parseFloat(a) - parseFloat(b);
        });

        return sortedValues.reduce(
            (acc, value) => ({ ...acc, [value]: value }),
            {}
        );
    }, [opportunities]);

    const fteDvmFilterProps = reactMultiselectFilter({
        placeholder: '',
        options: fteDvmOptions,
    }) as TableColumnFilterProps;

    const serviceFocusFilterProps = reactMultiselectFilter({
        placeholder: '',
        options: serviceFocuses.reduce(
            (acc, focus) => ({
                ...acc,
                [focus.abbreviation]: focus.abbreviation,
            }),
            {}
        ),
    }) as TableColumnFilterProps;

    const practiceTypeHeaderTooltipContent = (
        <ul>
            {serviceFocuses.map((option) => {
                const item = (
                    <li>{`${option.abbreviation} - ${option.name}`}</li>
                );
                return item;
            })}
        </ul>
    );

    const hasPermissions = (opportunity: MarketplaceOpportunity) => {
        const requiredPermissions = {
            isPrimaryBuyer: false,
            type: TeamMemberType.Internal,
            role: null,
            permission: InternalPermissions.ReadOnlyAndCommunicateAndExecute,
        } as TeamMemberPermissions;

        const currentPermissions = {
            isPrimaryBuyer: opportunity.isPrimaryBuyer,
            type: opportunity.teamMemberType,
            role: null,
            permission: opportunity.internalPermissions,
        } as TeamMemberPermissions;

        return TeamMemberPermissionsHelper.isAvailable(
            currentPermissions,
            requiredPermissions
        );
    };

    const getServiceFocusDisplayValue = (
        row: MarketplaceOpportunity
    ): string => {
        const mappedPracticeType = row.practices
            .map((practice) => {
                if (isOtherFocus(serviceFocuses, practice.practiceType)) {
                    return (
                        practice.otherPracticeType ||
                        getOtherFocus(serviceFocuses).abbreviation ||
                        ''
                    );
                }
                return (
                    serviceFocuses.find((f) => f.id === practice.practiceType)
                        ?.abbreviation ?? ''
                );
            })
            .filter((f) => f);
        return mappedPracticeType.join(', ');
    };

    const getLocationTypeDisplayValue = (
        row: MarketplaceOpportunity
    ): string => {
        const mappedPracticeType = row.practices
            .map(
                (practice) =>
                    LocationTypes.find((f) => f.id === practice.locationType)
                        ?.name ?? ''
            )
            .filter((f) => f);
        return mappedPracticeType.length ? mappedPracticeType.join(', ') : 'NA';
    };

    const getStateDisplayValue = (row: MarketplaceOpportunity): string => {
        const practices = row?.practices;
        if (!practices?.length) {
            return 'NA';
        }
        const statesOfRegistration = practices
            .filter((practice) => practice.stateOfRegistration)
            .map((practice) => stateToCodeMapper(practice.stateOfRegistration))
            .join(', ');
        return statesOfRegistration.length ? statesOfRegistration : 'NA';
    };

    const statusHeaderTooltipContent = (
        <ul>
            {MarketplaceOpportunityStatusList.map((option) => {
                const item = (
                    <li style={{ color: option.color }}>{option.name}</li>
                );
                return item;
            })}
        </ul>
    );

    const columns = [
        {
            dataField: 'opportunityId',
            text: 'Opp ID',
            headerClasses: 'opportunity-id-header',
            sort: true,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            Opp ID
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                </div>
            ),
            formatter: (_: any, row: MarketplaceOpportunity) => {
                return (
                    <span className="notification-flag-wrapper">
                        {row.opportunityId.toString()}
                        <NotificationFlag
                            eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${row.opportunityId}`}
                            includePrefix={true}
                        />
                    </span>
                );
            },
        },
        {
            dataField: 'daysOnMarket',
            text: 'Days on Mrkt',
            headerClasses: 'days-on-mrkt-header',
            classes: 'text-center',
            sort: true,
            formatter: (_: any, row: MarketplaceOpportunity) => {
                const debut = row.engagementData?.debutDate;
                if (!debut) {
                    return 'N/A';
                }

                const diff = moment().diff(moment(debut), 'days') + 1;
                return diff;
            },
        },
        {
            dataField: 'buyer.companyName',
            text: 'Buyer / Client',
            hidden: !isMPExternalMember,
            headerClasses: 'buyer-company-name-header',
            classes: 'text-center',
            sort: true,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            Buyer / Client
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                </div>
            ),
        },
        {
            dataField: 'practices.practiceType',
            text: 'Service Focus',
            headerClasses: 'practice-type-header',
            classes: 'text-center',
            sort: true,
            filter: serviceFocusFilterProps,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => {
                return (
                    <div className="header-content">
                        <div className="header-label">
                            <span>
                                Service Focus
                                <InfoTooltip
                                    idText="practice-type-tooltip"
                                    text=""
                                    html={ReactDOMServer.renderToString(
                                        practiceTypeHeaderTooltipContent
                                    )}
                                />
                                <SortingSymbol
                                    order={components.sortElement.props?.order}
                                />
                            </span>
                        </div>
                        <div className="header-filter">
                            {components.filterElement}
                        </div>
                    </div>
                );
            },
            formatter: (_: any, row: MarketplaceOpportunity) =>
                getServiceFocusDisplayValue(row),
        },
        {
            dataField: 'engagementFteDvms',
            text: 'FTE DVM',
            headerClasses: 'fte-dvm-header',
            classes: 'text-center',
            sort: true,
            filter: fteDvmFilterProps,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            FTE DVM
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                    <div className="header-filter">
                        {components.filterElement}
                    </div>
                </div>
            ),
        },
        {
            dataField: 'practices.locationType',
            text: 'Loc. Type',
            headerClasses: 'loc-type-header',
            classes: 'text-center',
            sort: true,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            Loc. Type
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                </div>
            ),
            formatter: (_: any, row: MarketplaceOpportunity) =>
                getLocationTypeDisplayValue(row),
        },
        {
            dataField: 'practices.stateOfRegistration',
            text: 'State',
            headerClasses: 'location-header',
            classes: 'text-center',
            sort: true,
            filter: locationFilterProps,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            State
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                    <div className="header-filter">
                        {components.filterElement}
                    </div>
                </div>
            ),
            formatter: (_: any, row: MarketplaceOpportunity) =>
                getStateDisplayValue(row),
        },
        {
            dataField: 'currentEvent',
            text: 'Current Stage',
            headerClasses: 'deal-stage-header',
            sort: true,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            Current Stage
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                </div>
            ),
            formatter: (cellContent: TimelineEvent) => {
                return cellContent ? <div>{cellContent?.stageName}</div> : 'NA';
            },
        },
        {
            dataField: 'status',
            text: 'Status',
            headerClasses: 'status-header',
            sort: true,
            filter: opportunityStatusFilterProps,
            headerFormatter: (
                _column: any,
                _colIndex: any,
                components: any
            ) => (
                <div className="header-content">
                    <div className="header-label">
                        <span>
                            Status
                            <InfoTooltip
                                idText="status-tooltip"
                                html={ReactDOMServer.renderToString(
                                    statusHeaderTooltipContent
                                )}
                                text={''}
                            />
                            <SortingSymbol
                                order={components.sortElement.props?.order}
                            />
                        </span>
                    </div>
                    <div className="header-filter">
                        {components.filterElement}
                    </div>
                </div>
            ),
            formatter: (
                cellContent: MarketplaceOpportunityStatus,
                row: MarketplaceOpportunity
            ) => {
                const statusConfig = MarketplaceOpportunityStatusList.find(
                    (x) => x.id == cellContent
                );
                const lastActor = row?.buyer?.lastActorInfo;
                return (
                    <div
                        style={{
                            color: statusConfig.color,
                            fontFamily: 'SegoeUI-Bold',
                        }}>
                        <span>{statusConfig.name}</span>
                        {lastActor ? (
                            <InfoTooltip
                                idText={`status-last-actor-${row.opportunityId}`}
                                html={ReactDOMServer.renderToString(
                                    <div>
                                        <div>
                                            {`Last actor: ${
                                                lastActor.actorName ?? 'N/A'
                                            }`}
                                        </div>
                                        <div>
                                            {`Updated: ${
                                                lastActor.lastModified
                                                    ? moment(
                                                          lastActor.lastModified
                                                      ).format(
                                                          'MM/DD/YYYY, h:mm A'
                                                      )
                                                    : 'N/A'
                                            }`}
                                        </div>
                                    </div>
                                )}
                                text={''}
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                );
            },
        },
        {
            dataField: '',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            classes: 'text-center',
            headerFormatter: () => (
                <div className="header-content">
                    <div className="header-label">
                        <span>Actions</span>
                    </div>
                </div>
            ),
            formatter: (_cellContent: any, row: MarketplaceOpportunity) => {
                if (!hasPermissions(row)) {
                    return <></>;
                }

                const isEngageButtonVisible =
                    row.isEngageAvailable &&
                    engagingAvailableStatuses.some((s) => s === row.status);

                const isTagButtonVisible =
                    row.isEngageAvailable &&
                    taggingAvailableStatuses.some((s) => s === row.status);

                const isRejectButtonVisible = rejectingAvailableStatuses.some(
                    (s) => s === row.status
                );

                return (
                    <div className="buttons-wrapper">
                        {isEngageButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    engage(row.opportunityId);
                                    event.stopPropagation();
                                }}>
                                {row.status ===
                                MarketplaceOpportunityStatus.RejectedByBuyer
                                    ? 'Re-Engage'
                                    : 'Engage'}
                            </Button>
                        ) : (
                            <> </>
                        )}

                        {isTagButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    tag(row.opportunityId);
                                    event.stopPropagation();
                                }}>
                                {'Tag'}
                            </Button>
                        ) : (
                            <></>
                        )}

                        {row.status === MarketplaceOpportunityStatus.Tagged ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    untag(row.opportunityId);
                                    event.stopPropagation();
                                }}>
                                {'Un-Tag'}
                            </Button>
                        ) : (
                            <></>
                        )}

                        {isRejectButtonVisible ? (
                            <Button
                                className="btn btn-primary"
                                onClick={(event) => {
                                    setSelectedOpportunityIdToReject(
                                        row.opportunityId
                                    );
                                    setIsConfirmationVisible(true);
                                    setRejectedPracticeName(
                                        row.practices.find((x) => x)
                                            ?.practiceName
                                    );
                                    event.stopPropagation();
                                }}>
                                {'Reject'}
                            </Button>
                        ) : (
                            <> </>
                        )}
                    </div>
                );
            },
        },
    ];

    const renderMappingComponent = () => {
        const mapHeader = (
            <div className="mb-3 page-header">
                <div className="header-title">{'Opportunities'}</div>
            </div>
        );
        return (
            <LandscapeMapContainer
                markers={markers}
                currentZoom={currentZoom}
                setCurrentZoom={setCurrentZoom}
                selectedMarkers={selectedMarkers}
                setSelectedMarkers={setSelectedMarkers}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
                setMarkers={setMarkers}
                markerVisibilityZoom={16}
                renderColorsDescription={renderColorsDescription}
                renderShapesDescription={renderShapesDescription}
                colMd={isFullScreen ? 12 : isMPExternalMember ? 5 : 6}
                mapHeader={mapHeader}
            />
        );
    };

    return (
        <div className="marketplace-opportunities-tab">
            <div>
                <div
                    className="opportunities-container"
                    style={{ flexDirection: isFullScreen ? 'column' : 'row' }}>
                    {renderMappingComponent()}
                    <Col
                        md={isFullScreen ? 12 : isMPExternalMember ? 7 : 6}
                        className="opportunities-table">
                        <BootstrapTable
                            bootstrap4
                            keyField="opportunityId"
                            data={opportunityState.opportunities.data}
                            columns={columns}
                            defaultSorted={defaultSorted}
                            filter={filterFactory()}
                            remote
                            headerClasses="header-vetrical-align-top"
                            pagination={paginationFactory({
                                page: opportunityState.opportunities.pagination
                                    .page,
                                sizePerPage:
                                    opportunityState.opportunities.pagination
                                        .sizePerPage,
                                sizePerPageList: [5, 10, 25, 50],
                                totalSize:
                                    opportunityState.opportunities.pagination
                                        .totalSize,
                            })}
                            noDataIndication="No Opportunities available"
                            selectRow={{
                                clickToSelect: true,
                                mode: 'radio',
                                selected: selectedOpportunity?.opportunityId
                                    ? [selectedOpportunity?.opportunityId]
                                    : [],
                                onSelect: (row: MarketplaceOpportunity) =>
                                    handleTableRowSelect(
                                        row.opportunityId,
                                        markers
                                    ),
                                classes: 'selected-row',
                                nonSelectableClasses: 'non-selectable-row',
                                hideSelectColumn: true,
                            }}
                            onTableChange={handleTableChange}
                        />
                    </Col>
                </div>
            </div>
            {isConfirmationVisible && (
                <div className="reject-confirmation-popup">
                    <ConfirmationDialogWithReason
                        isVisible={isConfirmationVisible}
                        setIsVisible={setIsConfirmationVisible}
                        action={reject}
                        reasonForAction={reasonForRejection}
                        setReasonForAction={(reason: string) => {
                            setReasonForRejection(reason);
                        }}
                        title={`You have elected to leave the process with ${rejectedPracticeName}. Any feedback you can provide regarding why you made this decision will be appreciated by the seller.`}
                        loader={false}
                    />
                </div>
            )}
        </div>
    );
};

export default MarketplaceOpportunities;
